import React, { useState, useEffect } from 'react';
import { StyledAsyncSelect, StyledInputContainer } from 'components/inputs/Select/CategorySearchSelect/Styled';
import ClearInputButton from 'components/Button/ClearInputButton';
import OptionMenu from 'components/inputs/Select/CategorySearchSelect/OptionMenu';
import { KeyValuePair } from 'utils/common';
import styled from 'styled-components';
import { coloursNames } from 'styling/colours';
import { icons } from 'assets/icons/index'
import { isEmpty } from 'lodash';
import { useOutsideAlerter } from 'hooks/useOutsideAlerter';
import { Label } from 'components/Label';

export const outdoorLocationString = 'Outside Mapped Buildings';

function checkSearchTermCharactersMatchString(searchTerm: string) {
  return outdoorLocationString.toLowerCase().includes(searchTerm.toLowerCase());
}

type CategorySearchSelectProps = {
  onSearch: (searchTerm: string, searchCategory: string) => Promise<any>
  onInputchange?: (searchTerm: string) => void
  searchCategoriesList: KeyValuePair[]
  categoryListTitle?: string
  placeholder: string
  id?: string
  disableSearchOnTextOnly?: boolean
}

export default function CategorySearchSelect({
  onSearch,
  searchCategoriesList,
  categoryListTitle,
  placeholder,
  onInputchange,
  id = "category-select",
  disableSearchOnTextOnly = false
}: CategorySearchSelectProps) {

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [inputHasFocus, setInputHasFocus] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false)
  const [latestSearchTerm, setLatestSearchTerm] = useState('')
  const [searchError, setSearchError] = useState('')
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const getSearchCategoryTitle = () => {
    return searchCategoriesList.find(r => r.value === category)?.key || ""
  }
  const showPlaceholder = getSearchCategoryTitle() !== '' && !inputHasFocus;

  const ref = React.useRef(null);
  useOutsideAlerter(ref, () => {
    setMenuIsOpen(false);
    setInputHasFocus(false);
  })

  const HandleSearch = (search: string, category: string = '') => {
    setSearchLoading(true)
    setLatestSearchTerm(search)
    onSearch(search, category)
      .then((error) => {
        if (error)
          setSearchError(error)
        setSearchLoading(false)
      })
  }

  useEffect(() => {
    if (search === "") {
      setMenuIsOpen(false);
      setInputHasFocus(false);
      setCategory('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  function handleInputChange(term: string, actionObj: any) {
    switch (actionObj.action) {
      case 'input-change':
        setSearch(term);
        setSearchError('')
        onInputchange && onInputchange(term)
        if (term === "" && latestSearchTerm !== "")
          HandleSearch(term)
        setMenuIsOpen(true);
        setInputHasFocus(true);
        break;
    }
  }

  function handleClickOptionSearch(e: React.MouseEvent | React.KeyboardEvent, selectedCategory: { value: string; label: string }) {
    e.stopPropagation();
    if (search.length < 1) return;

    setCategory(selectedCategory.value);
    if (selectedCategory.value === "is_outdoors")
      setSearch(outdoorLocationString)
    HandleSearch(search, selectedCategory.value)
    setMenuIsOpen(false);
    setInputHasFocus(false);
  }

  function handleEnterSearch(ev: React.KeyboardEvent) {
    if (ev.key !== 'Enter')
      return; // only call search on enter key.
    if (disableSearchOnTextOnly)
      return;
    const target: any = ev.target;
    const search = target.value.toString();

    if (search.length < 1) return;
    setSearch(search)
    setCategory('');
    HandleSearch(search);
    setMenuIsOpen(false);
  }

  function handleInputClick(ev: React.SyntheticEvent) {
    const target = ev.target as HTMLTextAreaElement;
    const targetClassName = target.classList[0];
    // prevent anything occuring if input was not clicked.
    if (isEmpty(targetClassName) || targetClassName.indexOf('asyncSearchPrefix__') === -1)
      return;
    setMenuIsOpen(search !== "");
    setInputHasFocus(true);
    setSearchError('')
  }

  const handleClear = () => {
    setSearch('')
    setSearchError('')
    if (latestSearchTerm !== "")
      HandleSearch('')
  }

  return (
    <StyledInputContainer
      ref={ref}
      className="input-container"
      //onClick on parent element to open menu, as React-Select does not expose an onclick event. 
      // A slightly hacky solution. a11y disabled because we do not want the parent div to be focusable.
      onClick={(ev: React.SyntheticEvent) => handleInputClick(ev)}
      role="document"
      width="370"
    >
      <Label
        id="category-select-label"
        htmlFor={`${id}-input`}
        value={placeholder}
        className="sr-only"
      />
      <StyledAsyncSelect
        selectedSearchMenuOptionLabel={getSearchCategoryTitle()}
        isLoading={searchLoading}
        id={id}
        arialabelledby="category-select-label"
        placeholder={placeholder}
        inputId={`${id}-input`}
        onInputChange={(newValue: string, action: any) => handleInputChange(newValue, action)}
        className={showPlaceholder ? 'asyncSearch --has-search' : 'asyncSearch'}
        classNamePrefix="asyncSearchPrefix"
        menuIsOpen={menuIsOpen}
        onKeyDown={(ev: any) => handleEnterSearch(ev)}
        inputValue={search}
        escapeClearsValue
        components={{
          Menu: (props: any) => {
            let mutatedMenuOptions = searchCategoriesList;
            // check searchterm string contains letters from outdoor search string.
            if (!checkSearchTermCharactersMatchString(search)) {
              // if it does not, remove isOutdoors entry from array.
              mutatedMenuOptions = searchCategoriesList.filter((item) => item.value !== 'is_outdoors');
            }
            return (
              <OptionMenu
                searchTerm={search}
                optionText="includes"
                title={categoryListTitle}
                menuOptions={mutatedMenuOptions}
                handleClickOptionSearch={handleClickOptionSearch}
                {...props}
              />
            );
          },
        }}
      />
      {search !== "" && <ClearInputButton onClick={handleClear} />}
      {searchError && (
        <ErrorStyledDiv>
          <icons.ErrorIcon aria-label="Error" />
          <span>{searchError}</span>
        </ErrorStyledDiv>
      )}
    </StyledInputContainer>
  );
}

export const ErrorStyledDiv = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: -44px;
  background: white;
  width: 100%;
  height: 40px;
  padding: 0 9px;
  z-index: 9999;
  border: 1px solid rgba(188, 190, 192, 0.3);
  border-radius: 3px;
  overflow: hidden;

  span {
    padding-left: 5px;
    color: ${coloursNames.cinnabarOrange};
  }
`;